import type { CrudFilter } from '@refinedev/core';
import { previousDay } from '@/utils/dateRange';

type LogicalFilter = Extract<CrudFilter, { field: string }>;
/** `CrudOperators` also covers `and`/`or`, which no field filter can use. */
export type FieldOperator = LogicalFilter['operator'];

export type ChipScalar = string | number | boolean;

/** How a chip's value reads: one value, a set, a range, or presence. */
export type ChipValue =
  | { kind: 'value'; value: ChipScalar }
  | { kind: 'values'; values: ChipScalar[]; excluded?: boolean }
  | { kind: 'range'; from?: ChipScalar; to?: ChipScalar }
  | { kind: 'presence'; present: boolean };

export interface FilterChip {
  /** Stable React key. */
  id: string;
  field: string;
  value: ChipValue;
  /** What `setFilters` has to clear for this chip to go away. */
  remove: { field: string; operator: FieldOperator }[];
}

const isLogical = (f: CrudFilter): f is LogicalFilter => 'field' in f;

const RANGE_OPERATORS: FieldOperator[] = ['gte', 'lte', 'lt'];

/**
 * The active filters as removable chips, so a table says what it is filtered
 * by — including filters that arrived in the URL rather than through its own
 * dropdowns.
 *
 * `ignore` is for fields that already have a visible control of their own, e.g.
 * the search box's `q`. `gte`/`lte` on one field collapse into a single range
 * chip that clears both ends at once.
 */
export const activeFilterChips = (
  filters: CrudFilter[],
  ignore: string[] = []
): FilterChip[] => {
  const active = filters.filter(
    (f) => isLogical(f) && f.value != null && !ignore.includes(f.field)
  ) as LogicalFilter[];

  const chips: FilterChip[] = [];
  const rangeFields = new Set<string>();

  for (const f of active) {
    if (RANGE_OPERATORS.includes(f.operator)) {
      if (rangeFields.has(f.field)) continue;
      rangeFields.add(f.field);
      const ends = active.filter((o) => o.field === f.field);
      const exclusiveTo = ends.find((o) => o.operator === 'lt')?.value;
      chips.push({
        id: `${f.field}|range`,
        field: f.field,
        value: {
          kind: 'range',
          from: ends.find((o) => o.operator === 'gte')?.value,
          // An exclusive end is written as the day after the one the user
          // picked; the chip has to name the day they picked.
          to:
            ends.find((o) => o.operator === 'lte')?.value ??
            (exclusiveTo != null ? previousDay(exclusiveTo) : undefined),
        },
        remove: RANGE_OPERATORS.map((operator) => ({
          field: f.field,
          operator,
        })),
      });
      continue;
    }

    const remove = [{ field: f.field, operator: f.operator }];
    const id = `${f.field}|${f.operator}`;
    if (f.operator === 'null' || f.operator === 'nnull') {
      chips.push({
        id,
        field: f.field,
        value: { kind: 'presence', present: f.operator === 'nnull' },
        remove,
      });
    } else if (f.operator === 'in' || f.operator === 'nin') {
      chips.push({
        id,
        field: f.field,
        // An exclusion read as a plain list would say the opposite of what it
        // filters by, so the chip carries the negation.
        value:
          f.operator === 'nin'
            ? { kind: 'values', values: f.value, excluded: true }
            : { kind: 'values', values: f.value },
        remove,
      });
    } else {
      chips.push({
        id,
        field: f.field,
        value: { kind: 'value', value: f.value },
        remove,
      });
    }
  }

  return chips;
};

/** Every filter the chips stand for — what a "clear all" action drops. */
export const chipsToClear = (
  chips: FilterChip[]
): { field: string; operator: FieldOperator; value: undefined }[] =>
  chips.flatMap((c) => c.remove.map((r) => ({ ...r, value: undefined })));

const ISO_DAY = /^\d{4}-\d{2}-\d{2}/;

/**
 * Fallback rendering for a value the caller did not translate: dates as the
 * user's locale writes them (both range filters emit ISO days), everything
 * else as-is.
 */
export const defaultChipValueLabel = (
  value: ChipScalar,
  locale?: string
): string =>
  typeof value === 'string' && ISO_DAY.test(value)
    ? new Date(value).toLocaleDateString(locale)
    : String(value);
